import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { Close, Palette } from '@mui/icons-material';
import type { Symbology, MarkerSymbol } from '../types/symbology';
import {
  DEFAULT_POLYGON_SYMBOLOGY,
  DEFAULT_LINE_SYMBOLOGY,
  DEFAULT_POINT_SYMBOLOGY,
  getDefaultSymbology,
} from '../types/symbology';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SymbologyEditorProps {
  /** Whether the dialog is open */
  open: boolean;
  /** The layer name shown in the dialog title */
  layerName: string;
  /** The geometry type of the layer — determines which controls are shown */
  geometryType: 'Point' | 'LineString' | 'Polygon';
  /** Current symbology to pre-populate the editor */
  currentSymbology?: Symbology;
  /** Called when the user clicks Apply with the new symbology */
  onApply: (symbology: Symbology) => void;
  /** Called when the user clicks Cancel or closes the dialog */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Clamp a number to [min, max] */
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Validate a hex color string (e.g. '#3388ff') */
const isValidHex = (color: string) => /^#[0-9A-Fa-f]{6}$/.test(color);

/** Supported marker symbols */
const MARKER_SYMBOLS: MarkerSymbol[] = ['circle', 'square', 'triangle', 'star', 'cross', 'x'];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ColorInputProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
}

/**
 * A combined color-picker + hex text field.
 * The native <input type="color"> provides the swatch; the TextField shows the hex value.
 */
const ColorInput: React.FC<ColorInputProps> = ({ label, value, onChange }) => {
  const [textValue, setTextValue] = useState(value);

  // Keep local text in sync when the parent value changes
  useEffect(() => {
    setTextValue(value);
  }, [value]);

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = e.target.value;
    setTextValue(hex);
    onChange(hex);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setTextValue(raw);
    // Only propagate valid hex values
    const normalized = raw.startsWith('#') ? raw : `#${raw}`;
    if (isValidHex(normalized)) {
      onChange(normalized);
    }
  };

  const handleTextBlur = () => {
    // On blur, normalise the displayed value back to the last valid color
    if (!isValidHex(textValue)) {
      setTextValue(value);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Native color picker swatch */}
      <Tooltip title="Pick color">
        <Box
          component="label"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '6px',
            border: '2px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            cursor: 'pointer',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isValidHex(value) ? value : '#cccccc',
          }}
        >
          <input
            type="color"
            value={isValidHex(value) ? value : '#cccccc'}
            onChange={handleColorPickerChange}
            style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
            aria-label={`${label} color picker`}
          />
          <Palette sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }} />
        </Box>
      </Tooltip>

      {/* Hex text field */}
      <TextField
        label={label}
        value={textValue}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        size="small"
        fullWidth
        error={!isValidHex(textValue) && textValue !== ''}
        helperText={
          !isValidHex(textValue) && textValue !== '' ? 'Enter a valid hex color (e.g. #3388ff)' : ''
        }
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * SymbologyEditor
 *
 * A modal dialog that lets the user edit the visual symbology of a vector layer.
 * Shows geometry-type-appropriate controls:
 *   - Polygon: fill color, stroke color, stroke width, opacity
 *   - LineString: stroke color, stroke width, opacity
 *   - Point: marker symbol, marker size, fill color (marker color), stroke color, opacity
 *
 * Integrates with the Redux `layersSlice` via the `onApply` callback.
 *
 * **Validates: Requirement 6** – Layer Symbology Modification
 */
const SymbologyEditor: React.FC<SymbologyEditorProps> = ({
  open,
  layerName,
  geometryType,
  currentSymbology,
  onApply,
  onCancel,
}) => {
  // ── Local state ──────────────────────────────────────────────────────────

  const getInitialSymbology = useCallback((): Symbology => {
    if (currentSymbology) return { ...currentSymbology };
    return getDefaultSymbology(geometryType);
  }, [currentSymbology, geometryType]);

  const [symbology, setSymbology] = useState<Symbology>(getInitialSymbology);

  // Reset local state whenever the dialog opens or the layer changes
  useEffect(() => {
    if (open) {
      setSymbology(getInitialSymbology());
    }
  }, [open, getInitialSymbology]);

  // ── Field updaters ───────────────────────────────────────────────────────

  const setFillColor = useCallback((color: string) => {
    setSymbology((prev) => ({ ...prev, fillColor: color }));
  }, []);

  const setStrokeColor = useCallback((color: string) => {
    setSymbology((prev) => ({ ...prev, strokeColor: color }));
  }, []);

  const setStrokeWidth = useCallback((width: number) => {
    setSymbology((prev) => ({ ...prev, strokeWidth: clamp(width, 0, 20) }));
  }, []);

  const setOpacity = useCallback((opacity: number) => {
    setSymbology((prev) => ({ ...prev, opacity: clamp(opacity, 0, 1) }));
  }, []);

  const setMarkerSymbol = useCallback((symbol: MarkerSymbol) => {
    setSymbology((prev) => ({ ...prev, markerSymbol: symbol }));
  }, []);

  const setMarkerSize = useCallback((size: number) => {
    setSymbology((prev) => ({ ...prev, markerSize: clamp(size, 1, 50) }));
  }, []);

  // ── Reset to defaults ────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    setSymbology(getDefaultSymbology(geometryType));
  }, [geometryType]);

  // ── Apply ────────────────────────────────────────────────────────────────

  const handleApply = useCallback(() => {
    onApply(symbology);
  }, [onApply, symbology]);

  // ── Derived values ───────────────────────────────────────────────────────

  const opacityPercent = Math.round((symbology.opacity ?? 1) * 100);
  const fillColor = symbology.fillColor ?? DEFAULT_POLYGON_SYMBOLOGY.fillColor;
  const strokeColor = symbology.strokeColor ?? DEFAULT_POLYGON_SYMBOLOGY.strokeColor;
  const strokeWidth = symbology.strokeWidth ?? 2;
  const markerSymbol = (symbology.markerSymbol ?? 'circle') as MarkerSymbol;
  const markerSize = symbology.markerSize ?? DEFAULT_POINT_SYMBOLOGY.markerSize;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="symbology-editor-title"
    >
      {/* ── Title bar ─────────────────────────────────────────────── */}
      <DialogTitle
        id="symbology-editor-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Palette color="primary" />
          <Box>
            <Typography variant="h6" component="span">
              Symbology Editor
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.2 }}
            >
              {layerName} · {geometryType}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onCancel} size="small" aria-label="Close symbology editor">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ── Content ───────────────────────────────────────────────── */}
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {/* ── Polygon controls ──────────────────────────────────── */}
          {geometryType === 'Polygon' && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -1 }}>
                Fill
              </Typography>
              <ColorInput label="Fill Color" value={fillColor} onChange={setFillColor} />

              <Divider />

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -1 }}>
                Stroke
              </Typography>
              <ColorInput label="Stroke Color" value={strokeColor} onChange={setStrokeColor} />

              <Box>
                <Typography variant="body2" gutterBottom>
                  Stroke Width: {strokeWidth}px
                </Typography>
                <Slider
                  value={strokeWidth}
                  onChange={(_, v) => setStrokeWidth(v as number)}
                  min={0}
                  max={20}
                  step={0.5}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                  ]}
                  valueLabelDisplay="auto"
                  aria-label="Stroke width"
                />
              </Box>
            </>
          )}

          {/* ── LineString controls ───────────────────────────────── */}
          {geometryType === 'LineString' && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -1 }}>
                Stroke
              </Typography>
              <ColorInput label="Stroke Color" value={strokeColor} onChange={setStrokeColor} />

              <Box>
                <Typography variant="body2" gutterBottom>
                  Stroke Width: {strokeWidth}px
                </Typography>
                <Slider
                  value={strokeWidth}
                  onChange={(_, v) => setStrokeWidth(v as number)}
                  min={0}
                  max={20}
                  step={0.5}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                  ]}
                  valueLabelDisplay="auto"
                  aria-label="Stroke width"
                />
              </Box>
            </>
          )}

          {/* ── Point controls ────────────────────────────────────── */}
          {geometryType === 'Point' && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -1 }}>
                Marker
              </Typography>

              <FormControl size="small" fullWidth>
                <InputLabel id="marker-symbol-label">Marker Symbol</InputLabel>
                <Select
                  labelId="marker-symbol-label"
                  value={markerSymbol}
                  label="Marker Symbol"
                  onChange={(e) => setMarkerSymbol(e.target.value as MarkerSymbol)}
                  inputProps={{ 'aria-label': 'Marker symbol' }}
                >
                  {MARKER_SYMBOLS.map((sym) => (
                    <MenuItem key={sym} value={sym}>
                      {sym.charAt(0).toUpperCase() + sym.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="body2" gutterBottom>
                  Marker Size: {markerSize}px
                </Typography>
                <Slider
                  value={markerSize}
                  onChange={(_, v) => setMarkerSize(v as number)}
                  min={1}
                  max={50}
                  step={1}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                  ]}
                  valueLabelDisplay="auto"
                  aria-label="Marker size"
                />
              </Box>

              <Divider />

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -1 }}>
                Color
              </Typography>
              <ColorInput label="Marker Color" value={fillColor} onChange={setFillColor} />
              <ColorInput label="Stroke Color" value={strokeColor} onChange={setStrokeColor} />

              <Box>
                <Typography variant="body2" gutterBottom>
                  Stroke Width: {strokeWidth}px
                </Typography>
                <Slider
                  value={strokeWidth}
                  onChange={(_, v) => setStrokeWidth(v as number)}
                  min={0}
                  max={10}
                  step={0.5}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                  ]}
                  valueLabelDisplay="auto"
                  aria-label="Stroke width"
                />
              </Box>
            </>
          )}

          {/* ── Opacity (all geometry types) ──────────────────────── */}
          <Divider />

          <Box>
            <Typography variant="body2" gutterBottom>
              Opacity: {opacityPercent}%
            </Typography>
            <Slider
              value={opacityPercent}
              onChange={(_, v) => setOpacity((v as number) / 100)}
              min={0}
              max={100}
              step={5}
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: 100, label: '100%' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              aria-label="Opacity"
            />
          </Box>

          {/* ── Preview swatch ────────────────────────────────────── */}
          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Preview
            </Typography>
            <SymbologyPreview symbology={symbology} geometryType={geometryType} />
          </Box>
        </Stack>
      </DialogContent>

      {/* ── Actions ───────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleReset} color="inherit" size="small">
          Reset to Default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Preview sub-component
// ---------------------------------------------------------------------------

interface SymbologyPreviewProps {
  symbology: Symbology;
  geometryType: 'Point' | 'LineString' | 'Polygon';
}

/**
 * A small SVG-based preview that renders a representative symbol
 * using the current symbology settings.
 */
const SymbologyPreview: React.FC<SymbologyPreviewProps> = ({ symbology, geometryType }) => {
  const opacity = symbology.opacity ?? 1;
  const fillColor = symbology.fillColor ?? '#3388ff';
  const strokeColor = symbology.strokeColor ?? '#ffffff';
  const strokeWidth = Math.min(symbology.strokeWidth ?? 2, 4); // cap for preview

  const svgStyle: React.CSSProperties = {
    border: '1px solid',
    borderColor: 'rgba(128,128,128,0.3)',
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.15)',
    display: 'block',
  };

  if (geometryType === 'Polygon') {
    return (
      <svg width="100%" height={60} style={svgStyle} aria-label="Polygon symbology preview">
        <rect
          x={20}
          y={10}
          width={260}
          height={40}
          fill={fillColor}
          fillOpacity={opacity}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          rx={4}
        />
      </svg>
    );
  }

  if (geometryType === 'LineString') {
    return (
      <svg width="100%" height={60} style={svgStyle} aria-label="LineString symbology preview">
        <line
          x1={20}
          y1={30}
          x2={280}
          y2={30}
          stroke={strokeColor}
          strokeWidth={Math.max(strokeWidth, 1)}
          strokeOpacity={opacity}
          strokeLinecap="round"
        />
      </svg>
    );
  }

  // Point
  const markerSymbol = symbology.markerSymbol ?? 'circle';
  const markerSize = Math.min(symbology.markerSize ?? 8, 24); // cap for preview
  const cx = 150;
  const cy = 30;

  const renderMarker = () => {
    switch (markerSymbol) {
      case 'circle':
        return (
          <circle
            cx={cx}
            cy={cy}
            r={markerSize}
            fill={fillColor}
            fillOpacity={opacity}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      case 'square':
        return (
          <rect
            x={cx - markerSize}
            y={cy - markerSize}
            width={markerSize * 2}
            height={markerSize * 2}
            fill={fillColor}
            fillOpacity={opacity}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      case 'triangle': {
        const h = markerSize * 1.732;
        const points = `${cx},${cy - markerSize} ${cx - markerSize},${cy + h / 2} ${cx + markerSize},${cy + h / 2}`;
        return (
          <polygon
            points={points}
            fill={fillColor}
            fillOpacity={opacity}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      }
      case 'star': {
        const outerR = markerSize;
        const innerR = markerSize * 0.4;
        const pts = Array.from({ length: 10 }, (_, i) => {
          const angle = (i * Math.PI) / 5 - Math.PI / 2;
          const r = i % 2 === 0 ? outerR : innerR;
          return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
        }).join(' ');
        return (
          <polygon
            points={pts}
            fill={fillColor}
            fillOpacity={opacity}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      }
      case 'cross': {
        const arm = markerSize;
        const t = Math.max(strokeWidth, 2);
        return (
          <g fill={strokeColor} fillOpacity={opacity}>
            <rect x={cx - t / 2} y={cy - arm} width={t} height={arm * 2} />
            <rect x={cx - arm} y={cy - t / 2} width={arm * 2} height={t} />
          </g>
        );
      }
      case 'x': {
        const arm = markerSize * 0.8;
        const t = Math.max(strokeWidth, 2);
        return (
          <g stroke={strokeColor} strokeWidth={t} strokeOpacity={opacity} strokeLinecap="round">
            <line x1={cx - arm} y1={cy - arm} x2={cx + arm} y2={cy + arm} />
            <line x1={cx + arm} y1={cy - arm} x2={cx - arm} y2={cy + arm} />
          </g>
        );
      }
      default:
        return (
          <circle
            cx={cx}
            cy={cy}
            r={markerSize}
            fill={fillColor}
            fillOpacity={opacity}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
    }
  };

  return (
    <svg width="100%" height={60} style={svgStyle} aria-label="Point symbology preview">
      {renderMarker()}
    </svg>
  );
};

export default SymbologyEditor;
